import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { formatDateIST } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/formatUtils';
import { 
  Download, Calendar, Search, Scale, Loader2, Plus, Trash2,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Package
} from 'lucide-react';
import { BackButton, PageSkeleton } from '../../components/common';

export const AdminGoldPurchaseTrackerPage = () => {
  const navigate = useNavigate();
  const [dailyReport, setDailyReport] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Add purchase dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addingPurchase, setAddingPurchase] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    gold_grams: '',
    amount_paid: '',
    vendor: '',
    invoice_number: '',
    notes: ''
  });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getGoldPurchaseTracker(startDate, endDate);
      setDailyReport(res.data.daily_report || []);
      setTotals(res.data.totals || {});
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/admin');
      } else {
        toast.error('Failed to load gold purchase tracker');
      }
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, navigate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleDownloadCSV = async () => {
    setCsvLoading(true);
    try {
      const res = await adminAPI.downloadGoldPurchaseCSV(startDate, endDate);
      
      const blob = new Blob([res.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded ${res.data.total_records} records`);
    } catch (error) {
      toast.error('Failed to download CSV');
    } finally {
      setCsvLoading(false);
    }
  };

  const handleAddPurchase = async () => {
    if (!purchaseForm.gold_grams || !purchaseForm.amount_paid) {
      toast.error('Please enter gold grams and amount paid');
      return;
    }
    
    setAddingPurchase(true);
    try {
      await adminAPI.addGoldPurchase({
        ...purchaseForm,
        gold_grams: parseFloat(purchaseForm.gold_grams),
        amount_paid: parseFloat(purchaseForm.amount_paid)
      });
      toast.success('Gold purchase recorded successfully');
      setShowAddDialog(false);
      setPurchaseForm({
        date: new Date().toISOString().split('T')[0],
        gold_grams: '',
        amount_paid: '',
        vendor: '',
        invoice_number: '',
        notes: ''
      });
      fetchReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record purchase');
    } finally {
      setAddingPurchase(false);
    }
  };

  const handleDeletePurchase = async (purchaseId) => {
    if (!window.confirm('Are you sure you want to delete this purchase record?')) return;
    
    try {
      await adminAPI.deleteGoldPurchase(purchaseId);
      toast.success('Purchase record deleted');
      fetchReport();
    } catch (error) {
      toast.error('Failed to delete purchase record');
    }
  };

  const goldBalance = totals.gold_balance || 0;
  const balanceStatus = goldBalance >= 0 ? 'surplus' : 'deficit';

  return (
    <div className="min-h-screen bg-cream pb-8" data-testid="admin-gold-purchase-page">
      {/* Header */}
      <div className="bg-white border-b border-border px-4 py-4 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton to="/admin" />
            <h1 className="font-serif text-xl text-navy font-semibold">Gold Purchase Tracker</h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddDialog(true)}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Record Purchase
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={csvLoading || dailyReport.length === 0}
              className="gap-2"
            >
              {csvLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Formula Info */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Scale className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-700">Gold Purchase Calculation Formula</p>
                <p className="text-sm text-blue-600 mt-1 font-mono">
                  (Millis Bought − Millis Sold − Millis Redeemed + Referral Bonus) ÷ 1000 = Gold Grams to Buy
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="mb-6 border-gold/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Filter by Date Range
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchReport} disabled={loading} className="w-full">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Apply Filter
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-5 h-5 text-green-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-green-700">{(totals.millis_bought || 0).toFixed(2)}</p>
              <p className="text-xs text-green-600">Millis Bought</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-4 text-center">
              <TrendingDown className="w-5 h-5 text-orange-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-orange-700">{(totals.millis_sold || 0).toFixed(2)}</p>
              <p className="text-xs text-orange-600">Millis Sold + Redeemed</p>
              <p className="text-[10px] text-orange-500">
                Sold: {(totals.millis_sold || 0).toFixed(2)} | Redeemed: {(totals.millis_redeemed || 0).toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4 text-center">
              <Scale className="w-5 h-5 text-purple-600 mx-auto mb-1" />
              <p className="text-lg font-bold text-purple-700">{(totals.gold_required || 0).toFixed(4)}g</p>
              <p className="text-xs text-purple-600">Gold Required</p>
            </CardContent>
          </Card>
          <Card className={`${balanceStatus === 'surplus' ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50'}`}>
            <CardContent className="p-4 text-center">
              {balanceStatus === 'surplus' ? (
                <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-600 mx-auto mb-1" />
              )}
              <p className={`text-lg font-bold ${balanceStatus === 'surplus' ? 'text-green-700' : 'text-red-700'}`}>
                {Math.abs(goldBalance).toFixed(4)}g
              </p>
              <p className={`text-xs ${balanceStatus === 'surplus' ? 'text-green-600' : 'text-red-600'}`}>
                {balanceStatus === 'surplus' ? 'Surplus (Excess)' : 'Deficit (Need to Buy)'}
              </p>
              <p className="text-[10px] text-muted-foreground">
                Purchased: {(totals.gold_purchased || 0).toFixed(4)}g
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Daily Report Table */}
        <Card className="border-gold/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-navy flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Daily Gold Requirement
              </span>
              <span className="text-sm font-normal text-muted-foreground">
                {dailyReport.length} days
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : dailyReport.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No transaction data found for the selected period</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3">Date</th>
                      <th className="text-right p-3">Bought</th>
                      <th className="text-right p-3">Sold</th>
                      <th className="text-right p-3">Redeemed</th>
                      <th className="text-right p-3">Referral</th>
                      <th className="text-right p-3">Net Millis</th>
                      <th className="text-right p-3">Gold Req.</th>
                      <th className="text-right p-3">Purchased</th>
                      <th className="text-right p-3">Cumulative</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyReport.map((day) => (
                      <tr key={day.date} className="border-b hover:bg-muted/30">
                        <td className="p-3 font-medium">{formatDateIST(day.date + 'T00:00:00Z')}</td>
                        <td className="p-3 text-right text-green-600">+{day.millis_bought?.toFixed(2)}</td>
                        <td className="p-3 text-right text-orange-600">-{day.millis_sold?.toFixed(2)}</td>
                        <td className="p-3 text-right text-purple-600">-{day.millis_redeemed?.toFixed(2)}</td>
                        <td className="p-3 text-right text-blue-600">+{day.millis_referral?.toFixed(2)}</td>
                        <td className="p-3 text-right font-medium">{day.net_millis?.toFixed(2)}</td>
                        <td className="p-3 text-right font-mono">{day.gold_required?.toFixed(4)}g</td>
                        <td className="p-3 text-right">
                          {day.gold_purchased > 0 ? (
                            <div>
                              <span className="text-green-600 font-medium">{day.gold_purchased?.toFixed(4)}g</span>
                              {day.purchase_records?.map(pr => (
                                <div key={pr.purchase_id} className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                                  <span>{formatCurrency(pr.amount_paid)}</span>
                                  <button
                                    onClick={() => handleDeletePurchase(pr.purchase_id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <div className="text-xs">
                            <p>Req: {day.cumulative_gold_required?.toFixed(4)}g</p>
                            <p className="text-green-600">Pur: {day.cumulative_gold_purchased?.toFixed(4)}g</p>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Purchase Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Record Gold Purchase</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={purchaseForm.date}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Gold (grams) *</Label>
                <Input
                  type="number"
                  step="0.0001"
                  placeholder="e.g., 10.5"
                  value={purchaseForm.gold_grams}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, gold_grams: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Amount Paid (INR) *</Label>
              <Input
                type="number"
                placeholder="e.g., 65000"
                value={purchaseForm.amount_paid}
                onChange={(e) => setPurchaseForm(prev => ({ ...prev, amount_paid: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input
                  placeholder="Vendor name"
                  value={purchaseForm.vendor}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, vendor: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Invoice No.</Label>
                <Input
                  placeholder="Invoice number"
                  value={purchaseForm.invoice_number}
                  onChange={(e) => setPurchaseForm(prev => ({ ...prev, invoice_number: e.target.value }))}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                placeholder="Any additional notes"
                value={purchaseForm.notes}
                onChange={(e) => setPurchaseForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPurchase} disabled={addingPurchase}>
              {addingPurchase ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Record Purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGoldPurchaseTrackerPage;
